import React, { useState, useContext, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthAtoms } from '@/features/login/hoc/AuthAtom';
import { useAtom } from 'jotai';
import { Auth } from 'aws-amplify';
import omiseLogo from '@/assets/omise.svg';
import '@/styles/header.css';

import TodoIcon from '@/assets/header_icon_inventory.svg';
import StorefrontRoundedIcon from '@/assets/header_icon_storefront.svg';
import CalculateIcon from '@/assets/header_icon_calculate.svg';
import FeedIcon from '@/assets/header_icon_newsmode.svg';
import FinanceSupportIcon from '@/assets/header_icon_finance_support.svg';
import DashboardRoundedIcon from '@/assets/header_icon_view_cozy.svg';
import TextSnippetIcon from '@/assets/header_icon_description.svg';
import AccountCircleIcon from '@/assets/account_circle.svg';
import CloseRoundedIcon from '@/assets/icon_close.svg';
import StorageRoundedIcon from '@/assets/header_icon_database.svg';
import MenuIcon from '@/assets/icon_menu.svg';
import CashRegisterIcon from '@/assets/header_icon_cash_register.svg';
import KitchenIcon from '@/assets/header_icon_kitchen.svg';
import KnowHowIcon from '@/assets/header_icon_know_how.svg';
import NewIcon from '@/assets/header_icon_new.svg';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RecruitingIcon from '@/assets/header_icon_recruiting.svg';
import SeminarIcon from '@/assets/header_icon_seminar.svg';
import { footerLinkList } from '@/assets/Const';
import { isDesignUserRoleAtom, isInsideSalesUserRoleAtom } from '@/stores/session/UserRoleAtom';
import { useAtomValue } from 'jotai';
import { selectedUserIdAtom } from '@/stores/selectedUser/SelectedUserAtom';
import { ConfigContext } from '@/config';
import { ButtonBack } from '../ButtonBack';

export const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPathName = location.pathname;
  const [isAuth, setIsAuth] = useAtom(AuthAtoms.isAuth);
  (async () => {
    try {
      await Auth.currentSession();
      setIsAuth(true);
    } catch (err) {
      setIsAuth(false);
    }
  })();

  // ユーザ権限
  const isDesignUserRole = useAtomValue(isDesignUserRoleAtom);
  const isInsideSalesUserRole = useAtomValue(isInsideSalesUserRoleAtom);
  const [, setSelectedUserId] = useAtom(selectedUserIdAtom);

  const config = useContext(ConfigContext);
  const omiseCraftStaticColumnsUrl = config?.omiseCraftStaticColumnsUrl ?? '';
  const siteMode = config?.siteMode ?? 'member';
  const omiseCraftWebinerColumnsUrl = omiseCraftStaticColumnsUrl + '/seminar';

  const generateFromPath = () => {
    if (location.pathname == '/login') {
      return location.state?.fromPath;
    } else {
      let result = location.pathname;
      const params = new URLSearchParams(location.search);
      const id = params.get('id');
      if (result == '/property' && id) {
        result = `${result}?id=${id}`;
      }
      return result;
    }
  };

  const headerList =
    !isDesignUserRole || !isInsideSalesUserRole
      ? [
          {
            path: '/properties/prefectures',
            url: undefined,
            label: '物件検索',
            icon: <img className="mb-[2px] header-icon" src={StorefrontRoundedIcon} alt="物件検索アイコン" />,
            auth: false,
            isNew: false
          },
          {
            path: '/design',
            url: undefined,
            label: '内装会社マッチング',
            icon: <img className="mb-[2px] header-icon" src={DashboardRoundedIcon} alt="内装会社マッチングアイコン" />,
            auth: false,
            isNew: false
          },
          {
            path: '/finance_support',
            url: undefined,
            label: '創業融資支援',
            icon: <img className="mb-[3px] header-icon" src={FinanceSupportIcon} alt="創業融資支援アイコン" />,
            auth: false,
            isNew: false
          },
          {
            path: '/kitchen',
            url: undefined,
            label: '厨房',
            icon: <img className="mb-[2px] header-icon" src={KitchenIcon} alt="厨房アイコン" />,
            auth: false,
            isNew: false
          },
          {
            path: '/cash_register',
            url: undefined,
            label: 'レジ・キャッシュレス',
            icon: <img className="mb-[3px] header-icon" src={CashRegisterIcon} alt="レジ・キャッシュレスアイコン" />,
            auth: false,
            isNew: false
          },
          {
            path: '/recruiting',
            url: undefined,
            label: '採用',
            icon: <img className="header-icon" src={RecruitingIcon} alt="採用アイコン" />,
            auth: false,
            isNew: false
          },
          {
            path: undefined,
            url: omiseCraftWebinerColumnsUrl,
            label: 'セミナー',
            icon: <img className="mb-[3px] header-icon" src={SeminarIcon} alt="セミナーアイコン" />,
            auth: false,
            isNew: false
          },
          {
            path: undefined,
            url: undefined,
            label: '開業ノウハウ',
            icon: <img className="mb-[1px] header-icon" src={KnowHowIcon} alt="開業ノウハウアイコン" />,
            auth: false,
            isNew: false
          }
        ]
      : [];

  const knowHowList = [
    {
      path: '/todo',
      url: undefined,
      label: '開業TODO',
      icon: <img className="header-icon" src={TodoIcon} alt="開業TODOアイコン" />,
      auth: false,
      isNew: false
    },
    {
      path: '/mypage/concept',
      url: undefined,
      label: '店舗コンセプト',
      icon: <img className="header-icon" src={FeedIcon} alt="店舗コンセプトアイコン" />,
      auth: false,
      isNew: false
    },
    {
      path: '/balance_simulation',
      url: undefined,
      label: '簡易損益シミュレーション',
      icon: <img className="header-icon" src={CalculateIcon} alt="簡易損益シミュレーションアイコン" />,
      auth: false,
      isNew: false
    },
    {
      path: undefined,
      url: omiseCraftStaticColumnsUrl,
      label: 'コラム',
      icon: <img className="header-icon" src={TextSnippetIcon} alt="コラムアイコン" />,
      auth: false,
      isNew: false
    },
    {
      path: '/partner',
      url: undefined,
      label: 'パートナーサービス',
      icon: <img className="header-icon" src={StorageRoundedIcon} alt="パートナーサービスアイコン" />,
      auth: false,
      isNew: false
    }
  ];

  // ログアウト
  const logout = async () => {
    try {
      await Auth.signOut();
      setIsAuth(false);
      // SessionStorageに保持した選択済み会員IDをクリア
      setSelectedUserId('');
      // location保持のためにnavigateで遷移
      navigate('/');
      // reloadでstateの破棄
      window.location.reload();
    } catch (error) {
      console.log('error signing out: ', error);
    }
  };

  // 現在のページチェック
  const checkCurrentPage = (path: string): boolean => {
    let ret = false;
    if (
      path == currentPathName ||
      (path != '/' && currentPathName.indexOf(path) > -1 && !currentPathName.includes('design_matching'))
    ) {
      ret = true;
    }
    return ret;
  };

  const checkSubPageAccent = (path: string | undefined): boolean => {
    if (path === currentPathName) {
      return true;
    }
    return false;
  };

  // ログイン状態によりリンク出し分け
  let userStateArea = {
    linkPath: '/login',
    linkLabel: 'ログイン'
  };
  if (siteMode == 'operator') {
    userStateArea = {
      linkPath: '/ope/login',
      linkLabel: 'ログイン'
    };
  }
  if (isAuth) {
    userStateArea = {
      linkPath: '/mypage',
      linkLabel: 'マイページ'
    };
    if (siteMode == 'operator') {
      userStateArea = {
        linkPath: '/ope/mypage',
        linkLabel: 'マイページ'
      };
    }
  }

  // SP用ハンバーガーメニュー切り替え
  const [openLinkMenuState, setOpenLinkMenuState] = useState(false);
  const [openUserMenuState, setOpenUserMenuState] = useState(false);

  const toggleLinkMenu = () => {
    setOpenLinkMenuState(!openLinkMenuState);
    setOpenUserMenuState(false);
  };

  const toggleUserMenu = () => {
    setOpenUserMenuState(!openUserMenuState);
    setOpenLinkMenuState(false);
  };

  const [isOpenKnowHow, setIsOpenKnowHow] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const [isSmallScreen, setIsSmallScreen] = useState(window.matchMedia('(max-width: 429px)').matches);

  useEffect(() => {
    if (!isOpenKnowHow) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(e.target as Node) && !isSmallScreen) {
        setIsOpenKnowHow(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpenKnowHow]);

  const handleResize = () => {
    setIsSmallScreen(window.matchMedia('(max-width: 429px)').matches);
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (openLinkMenuState) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [openLinkMenuState]);

  // SPメニュー用class
  const spLiClass = 'text-left font-bold bg-background mb-2 w-[310px] mx-auto ';

  return (
    <header>
      <nav className="relative header-bottom bg-contents md:h-fit mx-auto z-[90]">
        <div className="header-logo-box">
          <a
            href={isDesignUserRole ? '/ope/interior_design_orders/dashboard' : '/'}
            className="md:inline-block flex items-center md:grow-0 grow"
          >
            <img src={omiseLogo} className="logo header-logo" alt="omise logo" />
          </a>
          <div className="flex items-center">
            {!isDesignUserRole && (
              <a href="/about" className="font-bold header-about text-text md:block hidden">
                オミセクラフトとは
              </a>
            )}
            <div className="bg-background md:flex hidden header-gray-box shadow-[inset_3px_-3px_3px_#0000000D] opacity-100 userMenu">
              {!isDesignUserRole ? (
                <div className="flex items-center">
                  <Link
                    to={userStateArea.linkPath}
                    state={{ fromPath: generateFromPath() }}
                    className="font-bold text-main flex items-center"
                  >
                    <img className="header-icon" src={AccountCircleIcon} alt="ヘッダーアイコン" />
                    <span className="header-mypage">{userStateArea.linkLabel}</span>
                  </Link>
                </div>
              ) : currentPathName === '/' ? (
                <div className="flex items-center">
                  <Link
                    to={'/ope/mypage'}
                    state={{ fromPath: generateFromPath() }}
                    className="font-bold text-main flex items-center"
                  >
                    <img className="header-icon" src={AccountCircleIcon} alt="ヘッダーアイコン" />
                    <span className="header-mypage">{'マイページ'}</span>
                  </Link>
                </div>
              ) : (
                <div className="flex items-center">
                  {isDesignUserRole && generateFromPath().includes('/ope/interior_design_companys') ? (
                    <Link
                      to={'/ope/interior_design_orders/dashboard'}
                      state={{ fromPath: generateFromPath() }}
                      className="font-bold text-main flex items-center"
                    >
                      <img className="header-icon" src={AccountCircleIcon} alt="ヘッダーアイコン" />
                      <span className="header-mypage">{'案件一覧'}</span>
                    </Link>
                  ) : (
                    <Link
                      to={'/ope/interior_design_companys?id=0'}
                      state={{ fromPath: generateFromPath() }}
                      className="font-bold text-main flex items-center"
                    >
                      <img className="header-icon" src={AccountCircleIcon} alt="ヘッダーアイコン" />
                      <span className="header-mypage">{'各種設定'}</span>
                    </Link>
                  )}
                </div>
              )}
              {isAuth ? (
                <button
                  className="bg-accent rounded-3xl font-bold header-button flex justify-center items-center"
                  onClick={logout}
                >
                  <span className="header-button-text">ログアウト</span>
                </button>
              ) : (
                <React.Fragment>
                  {siteMode !== 'operator' && (
                    <Link
                      to="/regist/member_info"
                      state={{
                        fromPath: generateFromPath()
                      }}
                    >
                      <button className="bg-accent rounded-3xl font-bold header-button flex justify-center items-center">
                        <span className="header-button-text">新規会員登録</span>
                      </button>
                    </Link>
                  )}
                </React.Fragment>
              )}
            </div>
            <div
              onClick={toggleUserMenu}
              onKeyDown={toggleUserMenu}
              className="z-10 space-y-2 md:hidden cursor-pointer w-[60px] h-[60px]"
            >
              {/* SPユーザアイコン */}
              {openUserMenuState ? (
                <div className="bg-main menu flex items-center justify-center w-[60px] h-[60px]">
                  <span>
                    <img
                      className="inline w-[20px] h-auto color-svg-contents"
                      src={AccountCircleIcon}
                      alt="アカウントサークルアイコン"
                    />
                  </span>
                </div>
              ) : (
                <div className="menu flex items-center justify-center w-[60px] h-[60px]">
                  <span>
                    <img
                      className="inline w-[20px] h-auto color-svg-main"
                      src={AccountCircleIcon}
                      alt="アカウントサークルアイコン"
                    />
                  </span>
                </div>
              )}
            </div>
            <div
              onClick={toggleLinkMenu}
              onKeyDown={toggleLinkMenu}
              className="z-10 space-y-2 md:hidden cursor-pointer w-[60px] h-[60px]"
            >
              {/* SPリンクアイコン */}
              {openLinkMenuState ? (
                <div className="menu bg-main flex items-center justify-center w-[60px] h-[60px]">
                  <span>
                    <img
                      className="inline w-[24px] h-auto color-svg-contents"
                      src={CloseRoundedIcon}
                      alt="閉じるアイコン"
                    />
                  </span>
                </div>
              ) : (
                <div className="menu">
                  <span className="text-main flex items-center justify-center w-[60px] h-[60px]">
                    <img className="inline w-[20px] h-auto color-svg-main" src={MenuIcon} alt="メニューアイコン" />
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* PCメニュー */}
        <ul className={'md:flex justify-between header-gap mx-auto hidden text-sub linkMenu px-[80px]'}>
          {headerList.map((item, index) => {
            let liClass = 'header-list font-bold last:mr-0';
            if (!isAuth && item.auth) {
              liClass += ' text-disabled';
              return (
                <li className={liClass} key={Number(index)}>
                  {item.icon}
                  <span className="header-text">{item.label}</span>
                </li>
              );
            }
            if (item.path) {
              if (checkCurrentPage(item.path)) {
                liClass += ' text-main border-b-4 rounded-sm border-main fill-main';
              }
              return (
                <>
                  <li className={liClass} key={Number(index)}>
                    <a href={item.path}>
                      {item.icon}
                      <span className="header-text relative">{item.label}</span>
                    </a>
                    {item.isNew && (
                      <div className="new-logo">
                        <img src={NewIcon} className="filter-cancel" alt="newアイコン" />
                      </div>
                    )}
                  </li>
                </>
              );
            }
            if (item.url) {
              return (
                <li className={liClass} key={Number(index)}>
                  <a href={item.url} target="_blank" rel="noopener">
                    {item.icon}
                    <span className="header-text">{item.label}</span>
                  </a>
                </li>
              );
            }
            if (!item.path && !item.url) {
              return (
                <>
                  <div
                    className="flex flex-col"
                    onMouseEnter={() => setIsOpenKnowHow(true)}
                    onMouseLeave={() => setIsOpenKnowHow(false)}
                    ref={headerRef}
                  >
                    <li className={liClass + ' border-white border-b-4 rounded-sm cursor-pointer'} key={Number(index)}>
                      {item.icon}
                      <span className="header-text">{item.label}</span>
                    </li>
                    <div
                      className={`absolute right-0 top-full pt-[15px] px-[20px] bg-white shadow-lg rounded-bl-[15px] z-50 w-min-[200px] overflow-hidden transition-all duration-300 ${
                        isOpenKnowHow ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0 pointer-events-none'
                      }`}
                    >
                      <ul>
                        {knowHowList.map((subItem, subIndex) => (
                          <li
                            className={
                              checkSubPageAccent(subItem.path)
                                ? 'mb-[0.5vw] header-list font-bold last:mr-0 text-main'
                                : 'mb-[0.5vw] header-list font-bold last:mr-0 hover:text-main'
                            }
                            key={Number(subIndex)}
                          >
                            {subItem.path ? (
                              <a href={subItem.path} className="flex items-center">
                                {subItem.icon}
                                <span className="header-text relative ml-2">{subItem.label}</span>
                              </a>
                            ) : (
                              <a href={subItem.url} className="flex items-center" target="_blank" rel="noopener">
                                {subItem.icon}
                                <span className="header-text relative ml-2">{subItem.label}</span>
                              </a>
                            )}
                            {subItem.isNew && (
                              <div className="new-logo">
                                <img src={NewIcon} className="filter-cancel" alt="newアイコン" />
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </>
              );
            }
          })}
        </ul>
        {/* SPメニュー */}
        {
          /* ユーザメニュー */
          openUserMenuState ? (
            <ul className="md:hidden z-40 bg-contents w-full absolute spUserMenu">
              <li className={spLiClass} key="regist">
                <Link
                  to={userStateArea.linkPath}
                  state={{ fromPath: generateFromPath() }}
                  onClick={toggleUserMenu}
                  onKeyDown={toggleUserMenu}
                  className="w-full h-full block p-4"
                >
                  <span className="ml-2.5 text-sub !text-[14px]">{userStateArea.linkLabel}</span>
                </Link>
              </li>
              {isAuth ? (
                <li
                  className={spLiClass + ' p-4'}
                  key="logout"
                  onClick={() => {
                    logout();
                    toggleUserMenu();
                  }}
                  onKeyDown={() => {
                    logout();
                    toggleUserMenu();
                  }}
                >
                  <span className="ml-2.5 text-sub !text-[14px]">ログアウト</span>
                </li>
              ) : (
                <li className={spLiClass} key="regist">
                  <Link
                    to="/regist/member_info"
                    onClick={toggleUserMenu}
                    onKeyDown={toggleUserMenu}
                    state={{ fromPath: generateFromPath() }}
                    className="w-full h-full block p-4 "
                  >
                    <span className="ml-2.5 text-sub !text-[14px]">新規会員登録</span>
                  </Link>
                </li>
              )}
              <li
                className={spLiClass + ' text-main text-center p-4 cursor-pointer'}
                key="close"
                onClick={toggleUserMenu}
                onKeyDown={toggleUserMenu}
              >
                <span className="ml-2.5 !text-[14px]">閉じる</span>
              </li>
            </ul>
          ) : undefined
        }
        {
          /* リンクメニュー*/
          openLinkMenuState ? (
            <ul className="md:hidden z-40 bg-contents  w-full absolute spLinkMenu overflow-auto max-h-screen">
              {headerList.map((item, index) => {
                let spMainLiClass = spLiClass;

                if (!isAuth && item.auth) {
                  spMainLiClass += ' text-disabled';
                } else if (item.path && checkCurrentPage(item.path)) {
                  spMainLiClass += ' !text-[14px] text-main border-b-4 rounded-sm border-main';
                } else {
                  spMainLiClass += ' text-sub !text-[14px]';
                }
                if (!isAuth && item.auth) {
                  return (
                    <li className={spMainLiClass + ' p-4 '} key={Number(index)}>
                      {item.icon}
                      <span className="ml-2.5">{item.label}</span>
                    </li>
                  );
                }
                if (item.path) {
                  return (
                    <li className={spMainLiClass} key={Number(index)}>
                      <a
                        href={item.path}
                        className="w-full h-full block p-4 "
                        onClick={toggleLinkMenu}
                        onKeyDown={toggleLinkMenu}
                      >
                        {checkCurrentPage(item.path) ? (
                          <span className="color-svg-main">{item.icon}</span>
                        ) : (
                          <span className="color-svg-sub">{item.icon}</span>
                        )}

                        <span className="ml-2.5 relative">{item.label}</span>
                        {item.isNew ? (
                          <div className="new-logo">
                            <img src={NewIcon} className="filter-cancel" alt="newアイコン" />
                          </div>
                        ) : (
                          <></>
                        )}
                      </a>
                    </li>
                  );
                }
                if (item.url) {
                  return (
                    <li className={spMainLiClass} key={Number(index)}>
                      <a href={item.url} target="_blank" rel="noopener" className="w-full h-full block p-4">
                        {item.icon}
                        <span className="ml-2.5">{item.label}</span>
                      </a>
                    </li>
                  );
                }
                if (!item.path && !item.url) {
                  return (
                    <>
                      <li
                        className={spMainLiClass + ' !mb-0'}
                        key={Number(index)}
                        onClick={() => setIsOpenKnowHow(!isOpenKnowHow)}
                      >
                        <div className="w-full h-full block p-4 flex justify-between">
                          <div>
                            {item.icon}
                            <span className="ml-2.5">{item.label}</span>
                          </div>
                          {isOpenKnowHow ? '－' : '＋'}
                        </div>
                      </li>
                      {isOpenKnowHow ? (
                        knowHowList.map((subItem, subIndex) => (
                          <li className={spMainLiClass + ' !mb-0 border-t'} key={Number(subIndex)}>
                            {subItem.path ? (
                              <>
                                {checkCurrentPage(subItem.path) ? (
                                  <a href={subItem.path} className="flex items-center w-full h-full block p-4">
                                    <span className="flex items-center color-svg-main">{subItem.icon}</span>
                                    <span className="text-main header-text relative ml-2">{subItem.label}</span>
                                  </a>
                                ) : (
                                  <a href={subItem.path} className="flex items-center w-full h-full block p-4">
                                    <span className="flex items-center color-svg-sub">{subItem.icon}</span>
                                    <span className="header-text relative ml-2">{subItem.label}</span>
                                  </a>
                                )}
                              </>
                            ) : (
                              <a
                                href={subItem.url}
                                className="flex items-center w-full h-full block p-4"
                                target="_blank"
                                rel="noopener"
                              >
                                <span className="flex items-center color-svg-sub">{subItem.icon}</span>
                                <span className="header-text relative ml-2">{subItem.label}</span>
                              </a>
                            )}
                            {subItem.isNew && (
                              <div className="new-logo">
                                <img src={NewIcon} className="filter-cancel" alt="newアイコン" />
                              </div>
                            )}
                          </li>
                        ))
                      ) : (
                        <></>
                      )}
                    </>
                  );
                }
              })}
              <ul className="mt-10 flex flex-wrap justify-between">
                {footerLinkList.map((item) => (
                  <>
                    {item.label === '運営会社概要' ? (
                      <li className="text-center font-bold bg-background w-full mb-[4px]" key={item.path}>
                        <a
                          href={item.path}
                          key={item.path}
                          onClick={toggleLinkMenu}
                          onKeyDown={toggleLinkMenu}
                          className="p-4 w-full h-full block"
                        >
                          <span className="ml-2.5 text-sub !text-[14px]">{item.label}</span>
                          <OpenInNewIcon className="text-sub !text-[14px]" />
                        </a>
                      </li>
                    ) : (
                      <li className="text-center font-bold bg-background w-[49.5%] mb-[4px]" key={item.path}>
                        <a
                          href={item.path}
                          key={item.path}
                          onClick={toggleLinkMenu}
                          onKeyDown={toggleLinkMenu}
                          className="p-4 w-full h-full block"
                        >
                          <span className="ml-2.5 text-sub !text-[14px]">{item.label}</span>
                        </a>
                      </li>
                    )}
                  </>
                ))}
              </ul>
              <ButtonBack
                buttonClass=" text-sub text-xl block md:hidden mx-auto !pt-0 mt-[30px] w-[80%] max-w-[270px] sm:w-[270px] shadow border-orange-500 appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline"
                label="閉じる"
                onclickEvent={() => toggleLinkMenu()}
              />
            </ul>
          ) : undefined
        }
      </nav>
    </header>
  );
};
